import React from 'react';
import PropTypes from 'prop-types';
import { useCubeQuery } from '@cubejs-client/react';
import { Spin, Row, Col, Statistic, Table } from 'antd';
import {
  CartesianGrid,
  // PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
  LineChart,
  Line,
} from 'recharts';
import AmLineChart from "./amCharts/linechart"
import AmPieChart from "./amCharts/AmPieChart"
import AmBarChart from "./amCharts/AmBarChart"
import AmMap from './amCharts/AmMap'
import { Piechart } from '@ap-evolved/evolved-ui.charts.piechart'
import { Map } from '@ap-evolved/evolved-ui.charts.map'
import { colors } from '../theme/am4themes_myTheme';
const lib = 'amcharts'
let amchartIdGlobal;

const CartesianChart = ({ resultSet, children, ChartComponent }) => (
  <ResponsiveContainer width="100%" height={350}>
    <ChartComponent data={resultSet.chartPivot()}>
      <XAxis dataKey="x" />
      <YAxis />
      <CartesianGrid />
      {children}
      <Legend />
      <Tooltip />
    </ChartComponent>
  </ResponsiveContainer>
);

const stackedChartData = (resultSet) => {
  const data = resultSet
    .pivot()
    .map(({ xValues, yValuesArray }) =>
      yValuesArray.map(([yValues, m]) => ({
        x: resultSet.axisValuesString(xValues, ', '),
        color: resultSet.axisValuesString(yValues, ', '),
        measure: m && Number.parseFloat(m),
      }))
    )
    .reduce((a, b) => a.concat(b), []);
  return data;
};

const TypeToChartComponent = {
  line: ({ resultSet, pivotConfig }) => {
    return (
      <>
        {
          lib == 'amcharts' ?
            <AmLineChart
              resultSet={resultSet}
              pivotConfig={pivotConfig}
              amchartId={amchartIdGlobal}
              hasDateAxis
            /> :
            <CartesianChart resultSet={resultSet} ChartComponent={LineChart}>
              {resultSet.seriesNames().map((series, i) => (
                <Line
                  key={series.key}
                  stackId="a"
                  dataKey={series.key}
                  name={series.title}
                  stroke={colors[i]}
                />
              ))}
            </CartesianChart>
        }

      </>
    );
  },
  bar: ({ resultSet }) => {
    return (
      <CartesianChart resultSet={resultSet} ChartComponent={BarChart}>
        {resultSet.seriesNames().map((series, i) => (
          <Bar
            key={series.key}
            stackId="a"
            dataKey={series.key}
            name={series.title}
            fill={colors[i]}
          />
        ))}
      </CartesianChart>
    );
  },
  area: ({ resultSet }) => {
    return (
      <CartesianChart resultSet={resultSet} ChartComponent={AreaChart}>
        {resultSet.seriesNames().map((series, i) => (
          <Area
            key={series.key}
            stackId="a"
            dataKey={series.key}
            name={series.title}
            stroke={colors[i]}
            fill={colors[i]}
          />
        ))}
      </CartesianChart>
    );
  },
  pie: ({ resultSet }) => {
    return (
      <>
        {
          lib == 'amcharts' &&
          <AmPieChart resultSet={resultSet} amchartId={amchartIdGlobal} />
        }
      </>

    );
  },
  number: ({ resultSet }) => {
    return (
      <Row
        type="flex"
        justify="center"
        align="middle"
        style={{
          height: '100%',
        }}
      >
        <Col>
          {resultSet.seriesNames().map((s) => (
            <Statistic value={resultSet.totalRow()[s.key]} />
          ))}
        </Col>
      </Row>
    );
  },
  table: ({ resultSet, pivotConfig }) => {
    return (
      <Table
        pagination={false}
        columns={resultSet.tableColumns(pivotConfig)}
        dataSource={resultSet.tablePivot(pivotConfig)}
        // pagination={{ pageSize: 5 }}
      />
    );
  },
  map: ({ resultSet, pivotConfig }) => {
    return (
      <>
        <AmMap
          mapTitle=""
          seriesNames={resultSet.seriesNames()}
          data={resultSet.tablePivot(pivotConfig)}
          resultSet={resultSet}
        />
      </>
    )
  }
};


const TypeToMemoChartComponent = Object.keys(TypeToChartComponent)
  .map((key) => ({
    [key]: React.memo(TypeToChartComponent[key]),
  }))
  .reduce((a, b) => ({ ...a, ...b }));

const renderChart =
  (Component) =>
    ({ resultSet, error, pivotConfig }) =>
      (resultSet && (
        <Component resultSet={resultSet} pivotConfig={pivotConfig} />
      )) ||
      (error && error.toString()) || <Spin />;

const ChartRenderer = ({ vizState, amchartId, cubejsApi }) => {
  amchartIdGlobal = amchartId
  const { query, chartType, pivotConfig } = vizState;
  const component = TypeToMemoChartComponent[chartType];
  const renderProps = useCubeQuery(query);
  return component && renderChart(component)({ ...renderProps, pivotConfig });
};

ChartRenderer.propTypes = {
  vizState: PropTypes.object,
  cubejsApi: PropTypes.object,
};
ChartRenderer.defaultProps = {
  vizState: {},
  cubejsApi: null,
};
export default ChartRenderer;
